import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from './utils/supabase';
import { addToCart, removeFromCart, updateQuantity } from './utils/cart';
import { obtenerDolarAutomatico } from './utils/dolarUtils';
import { getArgentinaISOString, logTimezoneInfo } from './utils/timezoneUtils';
import Navbar from './components/Navbar';
import ProductCard from './components/ProductCard';
import ProductFilter from './components/ProductFilter';
import ProductDetail from './components/ProductDetail';
import Waves from './components/Waves';
import { fetchColorData, hasAvailableColors, getAvailableProducts } from './utils/colorDataDynamic';
import ColorSelectionModal from './components/ColorSelectionModal';
import CartDrawer from './components/CartDrawer';

function App() {
  // Inicializar el carrito desde localStorage
  const [cartItems, setCartItems] = useState(() => {
    const storedCart = localStorage.getItem('cartItems');
    return storedCart ? JSON.parse(storedCart) : [];
  });
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loadingColors, setLoadingColors] = useState(false);
  const [searchTerm, setSearchTerm] = useState('CELULARES');
  const [selectedBrand, setSelectedBrand] = useState('Todas');
  const [selectedSort, setSelectedSort] = useState('default');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [audioSubfilter, setAudioSubfilter] = useState('Todos');
  const [colorData, setColorData] = useState({});
  const [availableProducts, setAvailableProducts] = useState([]);
  const [showColorModal, setShowColorModal] = useState(false);
  const [colorModalProduct, setColorModalProduct] = useState(null);

  // Debug: Log del estado del modal
  useEffect(() => {
    console.log('🔍 Estado del modal de colores:', { showColorModal, colorModalProduct });
    if (showColorModal) {
      console.log('🎯 Modal debería estar visible ahora');
    }
  }, [showColorModal, colorModalProduct]);
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isFadingIn, setIsFadingIn] = useState(false);
  const [pendingFilter, setPendingFilter] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [cartHistory, setCartHistory] = useState(() => {
    const stored = localStorage.getItem('cartHistory');
    return stored ? JSON.parse(stored) : [];
  });
  const [favoritesVersion, setFavoritesVersion] = useState(0);
  const handleFavoritesChange = () => setFavoritesVersion(v => v + 1);

  const [allProducts, setAllProducts] = useState([]);

  // Marcas específicas a filtrar (deben coincidir con las categorías en la BD)
  const specificBrands = ["IPHONE", "SAMSUNG", "XIAOMI", "MOTO"];

  // Guardar carrito en localStorage cada vez que cambia
  useEffect(() => {
    localStorage.setItem('cartItems', JSON.stringify(cartItems));
  }, [cartItems]);

  // Guardar historial en localStorage cuando cambia
  useEffect(() => {
    localStorage.setItem('cartHistory', JSON.stringify(cartHistory));
  }, [cartHistory]);

  // Cargar productos desde Supabase al inicio
  useEffect(() => {
    async function fetchProducts() {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .order('name', { ascending: true });  // Ordenar por nombre desde la BD
      if (!error) {
        // Mapear 'categoria' a 'category' para compatibilidad con el frontend
        const mapped = data.map(p => {
          return {
            ...p,
            category: p.categoria,
            // price está en USD, pricears está en pesos
            price: p.price, // Precio en USD (se muestra principal)
            pricears: p.pricears // Precio en pesos (se muestra arriba)
          };
        });
        setAllProducts(mapped);
      }
    }
    fetchProducts();
  }, []);

  // Suscripción en tiempo real a cambios en productos (para reflejar cambios del panel admin)
  useEffect(() => {
    const channel = supabase
      .channel('products-changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'products' },
        (payload) => {
          setAllProducts(prev => {
            if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
              const p = payload.new;
              const mapped = {
                ...p,
                category: p.categoria,
                price: p.price,
                pricears: p.pricears
              };
              const others = prev.filter(x => x.id !== mapped.id);
              return [...others, mapped];
            }
            if (payload.eventType === 'DELETE') {
              const oldId = payload.old?.id;
              return prev.filter(x => x.id !== oldId);
            }
            return prev;
          });
        }
      )
      .subscribe();

    return () => {
      try {
        supabase.removeChannel(channel);
      } catch (e) {
        // noop
      }
    };
  }, []);

  // Cargar datos de colores una sola vez al inicio
  useEffect(() => {
    const loadColorData = async () => {
      try {
        setLoadingColors(true);
        const data = await fetchColorData();
        setColorData(data);

        // Cargar productos disponibles
        const available = await getAvailableProducts();
        setAvailableProducts(available);
      } finally {
        setLoadingColors(false);
      }
    };

    loadColorData();

    // Debug: Mostrar todas las categorías de productos
    console.log('📊 Todas las categorías de productos:');
    const categories = [...new Set(allProducts.map(p => p.category))];
    console.log(categories);

    // Debug: Mostrar productos de celulares
    const celulares = allProducts.filter(p =>
      p.category === 'Celulares' ||
      p.category === 'Apple' ||
      p.category === 'IPHONE' ||
      p.category === 'iPhone' ||
      p.category === 'SAMSUNG' ||
      p.category === 'Samsung' ||
      p.category === 'XIAOMI' ||
      p.category === 'Xiaomi' ||
      p.category === 'MOTO' ||
      p.category === 'Motorola'
    );
    console.log('📱 Productos de celulares encontrados:', celulares.map(p => ({ name: p.name, category: p.category })));
  }, []);

  // Función para actualizar el dólar en la base de datos
  const actualizarDolarEnBD = async (valorNum, fuente) => {
    try {
      // Aumentar 10 pesos a la cotización recibida por la API
      const valorConAjuste = valorNum + 10;
      const timestampActual = getArgentinaISOString();

      // Obtener el id de la fila actual (la de mayor id)
      const { data: filas, error: errorSelect } = await supabase
        .from('dolar')
        .select('id')
        .order('id', { ascending: false })
        .limit(1);

      if (!errorSelect && filas && filas.length > 0) {
        const idActual = filas[0].id;
        console.log('💰 Actualizando dólar en BD - ID:', idActual, 'Valor original:', valorNum, 'Valor ajustado:', valorConAjuste, 'Fecha:', timestampActual);
        const { error } = await supabase
          .from('dolar')
          .update({
            valor: valorConAjuste,
            updated_at: timestampActual
          })
          .eq('id', idActual);
        if (!error) {
          console.log('✅ Dólar actualizado exitosamente en la base de datos');
        } else {
          console.error('❌ Error al actualizar valor del dólar:', error);
        }
      } else {
        console.error('❌ No se encontró la fila de dólar para actualizar');
      }
    } catch (error) {
      console.error('❌ Error actualizando dólar en BD:', error);
    }
  };

  // Actualización automática del dólar al cargar la página
  useEffect(() => {
    const actualizarDolarAlCargar = async () => {
      console.log('🔄 Actualizando dólar al cargar la página...');
      logTimezoneInfo(); // Mostrar información de zona horaria
      try {
        const valorDolar = await obtenerDolarAutomatico();

        if (valorDolar && valorDolar > 0) {
          await actualizarDolarEnBD(valorDolar, 'Actualización al Cargar Página');
          console.log('✅ Dólar actualizado al cargar la página:', valorDolar);
        } else {
          console.log('⚠️ No se pudo obtener el dólar al cargar la página');
        }
      } catch (error) {
        console.error('❌ Error actualizando dólar al cargar la página:', error);
      }
    };

    actualizarDolarAlCargar();
  }, []);

  const FAVORITES_KEY = 'favoriteProducts';

  // Función optimizada para filtrar productos
  const filterProducts = useCallback(() => {
    // Filtro de favoritos: si searchTerm es 'FAVORITOS', mostrar solo favoritos y salir
    if (searchTerm === 'FAVORITOS') {
      const favs = (JSON.parse(localStorage.getItem(FAVORITES_KEY) || '[]')).map(String);
      setFilteredProducts(
        allProducts.filter(product =>
          favs.includes(String(product.id)) && product.visible !== false && product.stock !== false
        )
      );
      return;
    }

    let currentProducts = [...allProducts];

    // Filtrar solo productos visibles y con stock
    currentProducts = currentProducts.filter(product => product.visible !== false && product.stock !== false);

    // Mapeo de términos de búsqueda a categorías
    const searchMappings = {
      'CELULARES': ['IPHONE', 'iPhone', 'XIAOMI', 'Xiaomi', 'SAMSUNG', 'Samsung', 'MOTO', 'Motorola'],
      'ACCESORIOS': ['ACCESORIOS CELUS'],
      'APPLE': ['ECOSISTEMA APPLE'],
      'NOTEBOOKS': ['NOTEBOOKS'],
      'TABLETS': ['TABLETS'],
      'AUDIO': ['AUDIO-PARLANTES', 'AUDIO-AURIS', 'AUDIO-BARRAS DE SONIDO'],
      'SMARTWATCHES': ['SMARTWATCHES'],
      'ELECTRONICA': ['ELECTRONICA LIVIANA'],
      'CONSOLAS': ['CONSOLAS/GAMING']
    };

    // Función para normalizar categorías (mover fuera del scope condicional)
    const normalizeCat = (str = '') => str
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .replace(/\s+/g, ' ')   // colapsar espacios múltiples
      .trim()
      .toUpperCase();

    // Aplicar filtro de búsqueda por texto
    if (searchTerm && searchTerm.trim()) {
      const lowerCaseSearchTerm = searchTerm.toLowerCase().trim();
      if (searchMappings[searchTerm]) {
        // Filtrar por categoría específica (case-insensitive + sin acentos)
        const targets = searchMappings[searchTerm].map(t => normalizeCat(t));
        currentProducts = currentProducts.filter(product =>
          targets.includes(normalizeCat(product.category))
        );
      } else {
        // Búsqueda por texto libre
        currentProducts = currentProducts.filter(product =>
          product.name.toLowerCase().includes(lowerCaseSearchTerm) ||
          (product.description && product.description.toLowerCase().includes(lowerCaseSearchTerm)) ||
          (product.category && product.category.toLowerCase().includes(lowerCaseSearchTerm)) ||
          (product.brand && product.brand.toLowerCase().includes(lowerCaseSearchTerm))
        );
      }
    }

    // Filtro especial para AUDIO con subfiltros
    if (searchTerm === 'AUDIO') {
      const catUpper = (productCat) => normalizeCat(productCat);
      if (audioSubfilter === 'Parlantes') {
        currentProducts = currentProducts.filter(product =>
          catUpper(product.category) === 'AUDIO-PARLANTES'
        );
      } else if (audioSubfilter === 'Auriculares') {
        currentProducts = currentProducts.filter(product =>
          catUpper(product.category) === 'AUDIO-AURIS'
        );
      } else if (audioSubfilter === 'Barras de sonido') {
        currentProducts = currentProducts.filter(product =>
          catUpper(product.category) === 'AUDIO-BARRAS DE SONIDO'
        );
      }
    }

    // Filtro de marca solo para CELULARES
    if (searchTerm === 'CELULARES' && selectedBrand !== 'Todas') {
      // Mapear nombres de marca a categorías
      const brandMappings = {
        'IPHONE': ['IPHONE', 'iPhone'],
        'SAMSUNG': ['SAMSUNG', 'Samsung'],
        'XIAOMI': ['XIAOMI', 'Xiaomi'],
        'MOTO': ['MOTO', 'Motorola']
      };

      const targetCategories = brandMappings[selectedBrand] || [selectedBrand];
      currentProducts = currentProducts.filter(product =>
        targetCategories.includes(product.category)
      );
    }

    // Filtrar por stock usando los datos de colores ya cargados
    const productosConStock = currentProducts.filter(product => {
      // Si no hay datos de colores cargados, mostrar todos los productos
      if (!colorData || Object.keys(colorData).length === 0) {
        return true;
      }

      // Si es un producto de celulares, verificar que tenga colores disponibles
      if (product.category === 'Celulares' || product.category === 'Apple' || product.category === 'IPHONE' || product.category === 'iPhone' || product.category === 'SAMSUNG' || product.category === 'Samsung' || product.category === 'Xiaomi' || product.category === 'XIAOMI' || product.category === 'MOTO' || product.category === 'Motorola') {
        const productName = product.name.replace(/ DS$/, '');

        // Si no hay availableProducts cargados, mostrar el producto
        if (!availableProducts || availableProducts.length === 0) {
          return true;
        }

        // Buscar en availableProducts
        const availableProduct = availableProducts.find(ap => {
          const normalizedProductName = productName
            .normalize('NFD').replace(/\p{Diacritic}/gu, '')
            .replace(/\b(5g|4g|ds|con leica|sin stock|stock|gb|tb)\b/gi, '')
            .replace(/[+\-/().]/g, '')
            .replace(/\s+/g, '')
            .toLowerCase();

          const normalizedAvailableName = ap.name
            .normalize('NFD').replace(/\p{Diacritic}/gu, '')
            .replace(/\b(5g|4g|ds|con leica|sin stock|stock|gb|tb)\b/gi, '')
            .replace(/[+\-/().]/g, '')
            .replace(/\s+/g, '')
            .toLowerCase();

          return normalizedProductName === normalizedAvailableName ||
            normalizedProductName.includes(normalizedAvailableName) ||
            normalizedAvailableName.includes(normalizedProductName);
        });

        // Si no se encuentra en availableProducts, mostrar de todas formas
        // (esto permite que se muestren productos que pueden no estar en Google Sheets)
        return true;
      }

      // Para otros productos (no celulares), mostrar siempre
      return true;
    });

    // Aplicar ordenamiento
    if (selectedSort === 'price-asc') {
      productosConStock.sort((a, b) => (a.price || 0) - (b.price || 0));
    } else if (selectedSort === 'price-desc') {
      productosConStock.sort((a, b) => (b.price || 0) - (a.price || 0));
    } else {
      // Por defecto, ordenar por nombre (orden alfabético como en la lista original)
      productosConStock.sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }));
    }

    setFilteredProducts(productosConStock);
  }, [searchTerm, selectedBrand, selectedSort, audioSubfilter, colorData, availableProducts, favoritesVersion, allProducts]);

  // Ejecutar filtrado cuando cambien las dependencias
  useEffect(() => {
    filterProducts();
  }, [filterProducts]);

  const handleAddToCart = (product) => {
    setCartItems(prev => addToCart(prev, product));
    setSelectedProduct(null);
  };

  const handleUpdateQuantity = (productId, newQuantity) => {
    setCartItems(prev => updateQuantity(prev, productId, newQuantity));
  };

  const handleRemoveItem = (productId) => {
    setCartItems(prev => removeFromCart(prev, productId));
  };

  // Manejar transición al cambiar filtros/sección
  const triggerTransition = (filterCallback) => {
    setIsTransitioning(true);
    setIsFadingIn(false);
    setTimeout(() => {
      filterCallback();
      setIsTransitioning(false);
      setIsFadingIn(true);
      setTimeout(() => setIsFadingIn(false), 250); // Duración del fade-in
    }, 250); // Duración del fade-out
  };

  // Modificar handlers para usar transición
  const handleSearch = (term) => {
    triggerTransition(() => {
      setSearchTerm(term);
      setSelectedBrand('Todas');
      setAudioSubfilter('Todos');
    });
  };
  const handleFilterChange = (brand) => {
    triggerTransition(() => setSelectedBrand(brand));
  };
  const handleSortChange = (sortOption) => {
    triggerTransition(() => setSelectedSort(sortOption));
  };
  const handleGoHome = () => {
    triggerTransition(() => {
      setSearchTerm('CELULARES');
      setSelectedBrand('Todas');
      setSelectedSort('default');
      setSelectedProduct(null);
      setAudioSubfilter('Todos');
    });
  };

  const handleProductClick = (product) => {
    setSelectedProduct(product);
  };

  const handleCloseProductDetail = () => {
    setSelectedProduct(null);
  };

  // Handler para abrir el modal de color desde la grilla
  const handleRequestColorModal = (product) => {
    console.log('🔍 handleRequestColorModal llamado con:', product);
    console.log('📱 Categoría del producto:', product.category);

    if (product.category === 'Celulares' || product.category === 'Apple' || product.category === 'IPHONE' || product.category === 'iPhone' || product.category === 'SAMSUNG' || product.category === 'Samsung' || product.category === 'XIAOMI' || product.category === 'Xiaomi' || product.category === 'MOTO' || product.category === 'Motorola') {
      console.log('✅ Producto es celular, abriendo modal de colores');
      setColorModalProduct(product);
      setShowColorModal(true);
    } else {
      console.log('❌ Producto no es celular, agregando directamente al carrito');
      setCartItems(prev => addToCart(prev, product));
      setSelectedProduct(null);
    }
  };

  // Handler para agregar al carrito desde el modal de color
  const handleAddToCartWithColor = (productWithColor) => {
    setCartItems(prev => addToCart(prev, productWithColor));
    setShowColorModal(false);
    setColorModalProduct(null);
    setSelectedProduct(null);
  };

  // Al finalizar compra, guardar en historial
  const handleCheckoutComplete = (order) => {
    const currentItems = [...cartItems]; // Guardar copia antes de limpiar
    const total = currentItems.reduce((acc, item) => acc + (item.pricears * item.quantity), 0);

    // Limpiar carrito
    setCartItems([]);
    localStorage.removeItem('cartItems');

    // Guardar en historial
    const newEntry = {
      date: new Date().toLocaleString(),
      items: currentItems,
      total: total
    };
    setCartHistory(prev => [newEntry, ...prev]);
  };

  // Limpiar historial
  const handleClearHistory = () => {
    setCartHistory([]);
    localStorage.removeItem('cartHistory');
  };

  // Modal de historial
  const CartHistoryModal = () => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto relative">
        <button onClick={() => setShowHistory(false)} className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 p-1 rounded-full transition-colors">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <h2 className="text-2xl font-bold mb-4">Historial de Compras</h2>
        {cartHistory.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No hay historial de compras.</p>
        ) : (
          <div className="space-y-6">
            {cartHistory.map((entry, idx) => (
              <div key={idx} className="border rounded-lg p-4 bg-gray-50">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-gray-600">{entry.date}</span>
                  <span className="text-lg font-bold text-gray-900">Total: ${entry.total.toLocaleString('es-AR')}</span>
                </div>
                <ul className="divide-y divide-gray-200">
                  {entry.items.map((item, i) => (
                    <li key={i} className="py-2 flex items-center gap-3">
                      <img src={item.image ? (process.env.PUBLIC_URL + item.image) : (process.env.PUBLIC_URL + '/images/products/blank.jpg')} alt={item.name} className="w-10 h-10 object-contain rounded" />
                      <span className="font-medium text-gray-900">{item.name}</span>
                      {item.selectedColor && <span className="text-xs px-2 py-1 rounded bg-gray-200 ml-2">{item.selectedColor}</span>}
                      <span className="ml-auto text-gray-700">x{item.quantity}</span>
                      <span className="ml-4 text-gray-700">${item.pricears.toLocaleString('es-AR')}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
        <button onClick={handleClearHistory} className="mt-6 w-full py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-semibold">Limpiar Historial</button>
      </div>
    </div>
  );

  const handleCartDrawerOpen = () => {
    setCartDrawerOpen(true);
  };

  const handleCartDrawerClose = () => {
    setCartDrawerOpen(false);
  };

  const handleShowHistory = () => {
    setShowHistory(true);
  };

  // Manejar cierre de popups con ESCAPE (uno por vez)
  useEffect(() => {
    let escHandled = false;
    const handleEsc = (e) => {
      if (e.key === 'Escape' && !escHandled) {
        escHandled = true;
        // 1. Cerrar modal de color si está abierto
        if (showColorModal) {
          setShowColorModal(false);
          setColorModalProduct(null);
          return;
        }
        // 2. Cerrar detalle de producto si está abierto
        if (selectedProduct) {
          setSelectedProduct(null);
          return;
        }
        // 3. Cerrar carrito si está abierto
        if (cartDrawerOpen) {
          setCartDrawerOpen(false);
          return;
        }
        // 4. Cerrar historial si está abierto
        if (showHistory) {
          setShowHistory(false);
          return;
        }
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [showColorModal, selectedProduct, cartDrawerOpen, showHistory]);

  return (
    <div className="min-h-screen bg-gray-100 font-sans antialiased flex flex-col relative overflow-hidden">
      <Waves
        lineColor="#fff"
        backgroundColor="rgba(255, 255, 255, 0.2)"
        waveSpeedX={0.02}
        waveSpeedY={0.01}
        waveAmpX={60}
        waveAmpY={30}
        friction={0.9}
        tension={0.01}
        maxCursorMove={120}
        xGap={8}
        yGap={24}
        style={{ zIndex: 0 }}
      />
      <div style={{ position: 'relative', zIndex: 1 }}>
        <Navbar
          cartItems={cartItems}
          onUpdateQuantity={handleUpdateQuantity}
          onRemoveItem={handleRemoveItem}
          onSearch={handleSearch}
          onGoHome={handleGoHome}
          onCartClick={handleCartDrawerOpen}
        />

        <main className="flex-grow container mx-auto py-8 px-4 sm:px-6 lg:px-8 pt-24">
          <ProductFilter
            brands={searchTerm === 'CELULARES' ? specificBrands : (searchTerm === 'AUDIO' ? ['Todas'] : [])}
            onFilterChange={handleFilterChange}
            selectedBrand={searchTerm === 'CELULARES' || searchTerm === 'AUDIO' ? selectedBrand : 'Todas'}
            onSortChange={handleSortChange}
            selectedSort={selectedSort}
            showBrandFilter={searchTerm === 'CELULARES' || searchTerm === 'AUDIO'}
            audioSubfilter={searchTerm === 'AUDIO' ? audioSubfilter : null}
            setAudioSubfilter={searchTerm === 'AUDIO' ? setAudioSubfilter : null}
          />

          <div className={`mb-8 fade-products ${isTransitioning ? 'fade-out' : isFadingIn ? 'fade-in' : ''}`}>
            <h2 className="text-3xl font-bold text-gray-900 mb-6"></h2>
            {loadingColors ? (
              <div className="text-center text-gray-500 py-8">Cargando productos...</div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filteredProducts.length > 0 ? (
                  filteredProducts.map(product => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      onAddToCart={handleAddToCart}
                      onProductClick={() => handleProductClick(product)}
                      onRequestColorModal={handleRequestColorModal}
                      onFavoritesChange={handleFavoritesChange}
                    />
                  ))
                ) : (
                  <p className="col-span-full text-center text-gray-600 text-lg">No se encontraron productos que coincidan con tu búsqueda o filtros aplicados.</p>
                )}
              </div>
            )}
          </div>
        </main>

        {selectedProduct && (
          <ProductDetail
            product={selectedProduct}
            onAddToCart={handleAddToCart}
            onClose={handleCloseProductDetail}
            colorData={colorData}
          />
        )}

        {/* Modal de selección de color global */}
        {showColorModal && colorModalProduct && (
          <>
            {console.log('🎨 Renderizando ColorSelectionModal con producto:', colorModalProduct.name)}
            <ColorSelectionModal
              product={colorModalProduct}
              onClose={() => { setShowColorModal(false); setColorModalProduct(null); }}
              onAddToCart={handleAddToCartWithColor}
            />
          </>
        )}

        <CartDrawer
          isOpen={cartDrawerOpen}
          onClose={handleCartDrawerClose}
          cartItems={cartItems}
          onUpdateQuantity={handleUpdateQuantity}
          onRemoveItem={handleRemoveItem}
          onCheckoutComplete={handleCheckoutComplete}
          onShowHistory={handleShowHistory}
        />
        {showHistory && <CartHistoryModal />}

        <footer className="bg-gray-900 text-white py-8 mt-12">
          <div className="text-center text-gray-400 text-sm">
            © 2025 LT Celulares. Todos los derechos reservados.
          </div>
        </footer>
      </div>
      {/* Botón flotante de WhatsApp con efecto hover */}
      <style>{`
        .whatsapp-float-btn {
          position: fixed;
          bottom: 24px;
          right: 24px;
          z-index: 1000;
          background: #25D366;
          border-radius: 50%;
          width: 60px;
          height: 60px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 2px 8px rgba(0,0,0,0.2);
          transition: transform 0.2s, box-shadow 0.2s, background 0.2s;
        }
        .whatsapp-float-btn:hover {
          transform: scale(1.13);
          box-shadow: 0 4px 16px rgba(0,0,0,0.35);
          background: #1ebe57;
        }
        .fade-products {
          transition: opacity 0.25s;
        }
        .fade-products.fade-out {
          opacity: 0.15;
        }
        .fade-products.fade-in {
          opacity: 1;
        }
      `}</style>
      <a
        href="https://wa.me/5493412428484?text=¡Hola!%20Quiero%20hacer%20una%20consulta%20sobre%20LF%20Store."
        target="_blank"
        rel="noopener noreferrer"
        className="whatsapp-float-btn"
        aria-label="WhatsApp"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" viewBox="0 0 24 24" fill="white">
          <path d="M20.52 3.48A12.07 12.07 0 0 0 12 0C5.37 0 0 5.37 0 12c0 2.11.55 4.16 1.6 5.97L0 24l6.19-1.62A12.13 12.13 0 0 0 12 24c6.63 0 12-5.37 12-12 0-3.21-1.25-6.23-3.48-8.52zM12 22c-1.85 0-3.68-.5-5.25-1.44l-.38-.22-3.68.97.98-3.59-.25-.37A9.94 9.94 0 0 1 2 12c0-5.52 4.48-10 10-10s10 4.48 10 10-4.48 10-10 10zm5.2-7.8c-.28-.14-1.65-.81-1.9-.9-.25-.09-.43-.14-.61.14-.18.28-.7.9-.86 1.08-.16.18-.32.2-.6.07-.28-.14-1.18-.44-2.25-1.4-.83-.74-1.39-1.65-1.55-1.93-.16-.28-.02-.43.12-.57.12-.12.28-.32.42-.48.14-.16.18-.28.28-.46.09-.18.05-.34-.02-.48-.07-.14-.61-1.47-.84-2.01-.22-.53-.45-.46-.61-.47-.16-.01-.34-.01-.52-.01-.18 0-.48.07-.73.34-.25.27-.97.95-.97 2.3 0 1.35.99 2.65 1.13 2.83.14.18 1.95 2.98 4.74 4.06.66.28 1.18.45 1.58.58.66.21 1.26.18 1.73.11.53-.08 1.65-.67 1.88-1.32.23-.65.23-1.2.16-1.32-.07-.12-.25-.18-.53-.32z" />
        </svg>
      </a>
    </div>
  );
}

export default App;